import { ref, computed, getCurrentInstance } from 'vue';
import { useRouter } from 'vue-router';
import { useAppState } from './useAppState';
import { useCustomer } from './useCustomer';
import { useChat } from './useChat';
import { useApi } from './useApi';
import { getHttpInstance, getApiConfig } from './useApi';
/**
 * Shared chat panel logic used by AiChatBanner and ChatMgButton.
 * Owns the message list, send/format/idle/WhatsApp behaviour and the magic
 * dev commands. Component-specific bits (layout, expand/collapse, suggestion
 * chips, scroll target) stay in the component and plug in via options.
 */
export function useChatPanel(opts) {
    const router = useRouter();
    const state = useAppState();
    const { getCustomer } = useCustomer();
    const { sendChatMessage } = useChat();
    const { signalV1 } = useApi();
    const instance = getCurrentInstance();
    const whatsAppNumber = instance?.appContext.config.globalProperties.$whatsAppNumber || '';
    const message = ref(null);
    const messages = ref([]);
    const typing = ref(false);
    const idlePromptShown = ref(false);
    const userHasTyped = ref(false);
    const lastWhatsAppSentIndex = ref(0);
    const cachedVoices = ref([]);
    const idleTimeout = 20000;
    let inputIdleTimer = null;
    const prompts = [
        "Feel free to ask me anything, don't be shy 😊",
        "What can I help you with today?",
        "Ask away!",
        "How can I assist you today?",
        "What's on your mind today? 🧠",
        "I'm here to help with your financial questions. 🙋‍♀️",
        "Let's talk about your goals and how I can assist you.",
        "Let's talk finances. 💬",
        "Here to help with your financial journey. 🛤️",
        "I'm ready when you are. ✅",
        "You wait - time passes. ⏳",
        "How can I help you manage your money? 🤝",
        "Ask me anything about your finances. 🤔",
        "Your financial well-being is my priority. 💼",
        "Let's work together to achieve your financial goals."
    ];
    if (window.speechSynthesis) {
        cachedVoices.value = window.speechSynthesis.getVoices();
        window.speechSynthesis.onvoiceschanged = () => {
            cachedVoices.value = window.speechSynthesis.getVoices();
        };
    }
    const hasUnsentMessages = computed(() => {
        return messages.value.slice(lastWhatsAppSentIndex.value).some(m => !m.whatsAppConfirmation);
    });
    function pushDisclaimer() {
        messages.value.push({
            direction: "in",
            body: "Please remember, the Digital Financial Assistant provides factual information to help you make informed decisions. " +
                "Any specific actions it may suggest should be taken as guidance, and not advice."
        });
        lastWhatsAppSentIndex.value = messages.value.length;
        getCustomer(() => { });
    }
    function onKeyPress(event) {
        if (!userHasTyped.value && event.key.length === 1 && event.key.match(/\S/)) {
            userHasTyped.value = true;
        }
        if (event.key === "Enter") {
            if (!message.value || message.value.trim() === "") {
                event.preventDefault();
                return;
            }
            showSentMessage();
            sendMessageAndWaitForResponse(message.value);
            message.value = "";
            idlePromptShown.value = false;
        }
    }
    function showSentMessage() {
        messages.value.push({ direction: "out", body: message.value });
        typing.value = true;
        opts.scrollToEnd();
    }
    /** Hidden dev/navigation commands. Returns true if the message was a command. */
    function handleMagicCommand(msg) {
        const m = msg.toLowerCase();
        if (m == "litmustest") {
            router.push('/litmustestpage');
            return true;
        }
        if (m == "allownoti") {
            signalV1("enableNotifications", "", "", "", "activity");
            return true;
        }
        if (m == "testpage") {
            router.push('/test');
            return true;
        }
        if (m == "hiddensettings") {
            const current = localStorage.getItem('showHiddenSettings') === 'true';
            localStorage.setItem('showHiddenSettings', (!current).toString());
            localStorage.setItem('useLocalCache', 'true');
            localStorage.setItem('useCachedEndpoints', 'true');
            router.push('/');
            return true;
        }
        if (m == "speak") {
            const current = localStorage.getItem('speak') === 'true';
            localStorage.setItem('speak', (!current).toString());
            return true;
        }
        if (m == "spendingtreemap") {
            router.push('/spendingtreemap');
            return true;
        }
        if (m == "payslips") {
            localStorage.setItem('showPayslips', 'true');
            router.push('/payslips');
            return true;
        }
        if (m == "admin") {
            router.push('/admin');
            return true;
        }
        if (m == "chatpage") {
            router.push('/chat');
            return true;
        }
        if (m == "spendingv2") {
            router.push('/SpendingAnalysis');
            return true;
        }
        if (m == "redeem") {
            router.push('/redeem');
            return true;
        }
        if (m == "mgm") {
            router.push('/you');
            return true;
        }
        if (m == "address") {
            router.push('/address');
            return true;
        }
        const voucherCardMatch = m.match(/^vouchercard([1-4])$/);
        if (voucherCardMatch) {
            localStorage.setItem('showNewVoucherCard', 'true');
            window.location.href = '/#/?voucherState=' + voucherCardMatch[1];
            window.location.reload();
            return true;
        }
        if (m == "vouchercard") {
            localStorage.setItem('showNewVoucherCard', 'true');
            window.location.href = '/#/';
            window.location.reload();
            return true;
        }
        if (m == "vouchercardoff") {
            localStorage.setItem('showNewVoucherCard', 'false');
            window.location.href = '/#/';
            window.location.reload();
            return true;
        }
        if (m == "signin") {
            window.location.href = '/#/signin';
            return true;
        }
        const quizTestMatch = m.match(/^test question (\d+)$/);
        if (quizTestMatch) {
            router.push({ path: '/', query: { quiztest: 'true', q: quizTestMatch[1] } });
            return true;
        }
        return false;
    }
    function sendMessageAndWaitForResponse(msg, store = true) {
        if (handleMagicCommand(msg))
            return;
        typing.value = true;
        opts.onSend?.();
        sendChatMessage(msg, (res) => {
            if (!res.keepThinking)
                typing.value = false;
            const messageIn = res.body.replaceAll("**", '');
            messages.value.push({ direction: "in", body: messageIn, store: store });
            if (!res.keepThinking)
                opts.onReply?.(messageIn, res);
            if (localStorage.getItem('speak') === 'true') {
                const utterance = new SpeechSynthesisUtterance(messageIn);
                utterance.rate = 0.9;
                utterance.pitch = 0.8;
                utterance.voice = cachedVoices.value.find(voice => voice.lang === 'en-GB') || null;
                window.speechSynthesis.speak(utterance);
            }
            opts.scrollToEnd();
        });
    }
    function sendMessage() {
        if (message.value && !typing.value) {
            showSentMessage();
            sendMessageAndWaitForResponse(message.value);
            message.value = "";
            idlePromptShown.value = false;
            userHasTyped.value = true;
            resetInputIdleTimer();
        }
    }
    function formatMessage(text) {
        if (!text)
            return '';
        let html = text
            .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
            .replace(/\*([^*]+)\*/g, '<em>$1</em>');
        html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>');
        html = html.replace(/(<br>)+\s*(\d+\.) /g, '<br>$2 ');
        html = html.replace(/\n\s*- /g, '<br>&bull; ');
        html = html.replace(/\n/g, '<br>');
        return html;
    }
    function processMonetaryAmounts(text) {
        if (localStorage.getItem('randomiseFigures') !== 'true')
            return text;
        const monetaryPattern = /(?:£|$)(?:\d{1,3}(?:,\d{3})*(?:\.\d{2})?|\d+(?:\.\d{2})?)|(?:\d+(?:\.\d{2})?\s*(?:pounds?|pence|p|GBP|USD))/gi;
        return text.replace(monetaryPattern, (match) => {
            if (match.startsWith('£') || match.startsWith('$'))
                return match.charAt(0) + '####';
            return '####' + match.replace(/^\d+(?:\.\d{2})?/, '');
        });
    }
    function applyMonetaryMask(text) {
        if (localStorage.getItem('randomiseFigures') === 'true')
            return processMonetaryAmounts(text);
        return text;
    }
    function resetInputIdleTimer() {
        if (inputIdleTimer)
            clearTimeout(inputIdleTimer);
        if (idlePromptShown.value || userHasTyped.value)
            return;
        inputIdleTimer = setTimeout(() => {
            if (!typing.value && (!message.value || message.value.trim() === '') && !idlePromptShown.value && opts.isOpen() && !userHasTyped.value) {
                typing.value = true;
                setTimeout(() => {
                    typing.value = false;
                    messages.value.push({ direction: "in", body: prompts[Math.floor(Math.random() * prompts.length)] });
                    idlePromptShown.value = true;
                    opts.scrollToEnd();
                }, 2000);
            }
        }, idleTimeout);
    }
    function clearIdleTimer() {
        if (inputIdleTimer)
            clearTimeout(inputIdleTimer);
    }
    function sendWhatsAppNotification(body, phoneNumber) {
        const request = { body, whatsApp: true, whatsAppTemplate: false, phoneNumber };
        getHttpInstance().post(getApiConfig().api + "/Customers/00000000-0000-0000-0000-000000000000/Notifications", request)
            .catch((result) => { console.log(result, 'WhatsApp notification error'); });
    }
    function attach() {
        if (state.customer) {
            if (state.customer.person.whatsAppEnabled) {
                messages.value.push({ direction: "in", body: "Check your WhatsApp!" });
                opts.scrollToEnd();
                sendWhatsAppNotification("You indicated you wanted to send a document 📄  Just drop it here and we will upload and process it for you 👍", state.customer.person.mobilePhone);
                window.location.href = `https://wa.me/${whatsAppNumber}`;
            }
            else {
                router.push('/enablewhatsapp');
            }
        }
    }
    function sendToWhatsApp() {
        if (state.customer) {
            if (state.customer.person.whatsAppEnabled) {
                const newMessages = messages.value.slice(lastWhatsAppSentIndex.value).filter(m => !m.whatsAppConfirmation);
                if (newMessages.length === 0)
                    return;
                const conversation = newMessages.map((m) => (m.direction === 'out' ? 'You' : 'MG') + ': ' + m.body).join('\n');
                lastWhatsAppSentIndex.value = messages.value.length;
                messages.value.push({ direction: "in", body: "Conversation sent to your WhatsApp!", whatsAppConfirmation: true });
                opts.scrollToEnd();
                sendWhatsAppNotification('Conversation saved from MGChat:\n\n' + conversation, state.customer.person.mobilePhone);
                window.location.href = `https://wa.me/${whatsAppNumber}`;
            }
            else {
                router.push('/#/enablewhatsapp');
            }
        }
    }
    // Stops iOS from scrolling the page when focusing the chat input.
    let chatTouchStart = null;
    function installIosScrollGuard() {
        if (chatTouchStart)
            return;
        chatTouchStart = (e) => {
            const target = e.target;
            const wrapper = target?.closest('.chatInput');
            if (!wrapper)
                return;
            const input = wrapper.querySelector('input');
            if (!input || input === document.activeElement)
                return;
            e.preventDefault();
            input.focus({ preventScroll: true });
        };
        document.addEventListener('touchstart', chatTouchStart, { passive: false });
    }
    function removeIosScrollGuard() {
        if (chatTouchStart) {
            document.removeEventListener('touchstart', chatTouchStart);
            chatTouchStart = null;
        }
    }
    return {
        // state
        message,
        messages,
        typing,
        idlePromptShown,
        userHasTyped,
        lastWhatsAppSentIndex,
        cachedVoices,
        prompts,
        hasUnsentMessages,
        // methods
        pushDisclaimer,
        onKeyPress,
        showSentMessage,
        sendMessageAndWaitForResponse,
        sendMessage,
        formatMessage,
        processMonetaryAmounts,
        applyMonetaryMask,
        resetInputIdleTimer,
        clearIdleTimer,
        sendWhatsAppNotification,
        attach,
        sendToWhatsApp,
        installIosScrollGuard,
        removeIosScrollGuard,
    };
}
